<template>
  <a-modal
    :open="open"
    title="Crear nueva plantilla de correo"
    centered
    width="min(800px, 70vw)"
    @cancel="handleClose"
  >
    <section class="container">
      <h2 class="title">{{ stepTexts.title }}</h2>

      <p class="description">{{ stepTexts.description }}</p>

      <div class="buttons" v-if="currentStep === 0">
        <TemplateTypeCard :type="TemplateType.Default" @click="handleClickTypeCard" />

        <TemplateTypeCard :type="TemplateType.Html" @click="handleClickTypeCard" />
      </div>

      <TemplateDataForm v-else ref="templateFormRef" :templateType="templateType" />

      <div class="info">
        <span class="info-title"> ¿Necesitas ayuda para comenzar? </span>

        <span class="links">
          <a
            href="https://beexcc.com/docs/contact-center/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visita nuestra documentación <ExportOutlined />
          </a>

          <!-- <a-divider type="vertical" style="background-color: #d9d9d9" />

          <a href="http://" target="_blank" rel="noopener noreferrer">
            Ver nuestro centro de ayuda
          </a> -->
        </span>
      </div>
    </section>

    <template #footer>
      <footer v-if="currentStep === 1">
        <a-button @click="handleClose">Cancelar</a-button>
        <a-button type="primary" :loading="loading" @click="handleCreate">Crear plantilla</a-button>
      </footer>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { ExportOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";

import { TemplateType } from "@/@types/flows/emailTemplates";
import { CreateTemplateDto } from "@/@types/flows/emailTemplates/dtos";
import { emailTemplateService } from "@/app/flows/emailTemplates/services/EmailTemplate.service";
import { ResponseError } from "@/app/shared/utils/httpManager";

import TemplateTypeCard from "@/app/flows/emailTemplates/components/cards/TemplateTypeCard.vue";
import TemplateDataForm from "@/app/flows/emailTemplates/components/forms/TemplateDataForm.vue";

defineProps<{ open: boolean }>();

const emit = defineEmits<{ "update:open": [open: boolean] }>();

const router = useRouter();

const currentStep = ref(0);
const templateType = ref<TemplateType>();

const stepTexts = computed<{ title: string; description: string }>(() => {
  if (!templateType.value)
    return {
      title: "¿Cómo deseas crear tu plantilla?",
      description:
        "Elige cómo crear tu plantilla de correo. Estas podrán ser enviadas de forma masiva desde el módulo de estrategias. ",
    };

  return {
    title:
      templateType.value === TemplateType.Default
        ? "Datos generales de la plantilla"
        : "Genera tu plantilla HTML",
    description:
      templateType.value === TemplateType.Default
        ? "Completa los datos generales de tu plantilla para que puedas ubicarla y reconocerla con mayor facilidad. "
        : "Completa los datos generales de tu plantilla e importa tu archivo HTML.",
  };
});

const handleClickTypeCard = (type: TemplateType) => {
  templateType.value = type;
  currentStep.value++;
};

const templateFormRef = ref<InstanceType<typeof TemplateDataForm>>();
let templateContent: string;

watch(
  () => templateFormRef.value?.formState.html_file,
  (templateFile) => {
    if (!templateFile) return;

    const reader = new FileReader();

    reader.onload = (file) => {
      const fileContent = file.target?.result;

      if (!fileContent) return;
      if (typeof fileContent !== "string") return;

      console.log(fileContent);
      templateContent = fileContent;
    };

    reader.readAsText(templateFile);
  }
);

const loading = ref(false);

const handleCreate = async () => {
  if (!templateFormRef.value) return;

  await templateFormRef.value.validate();
  const formState = templateFormRef.value.formState;

  loading.value = true;

  try {
    if (!templateType.value) throw new Error("No template selected");

    const createDto: Partial<CreateTemplateDto> = {
      name: formState.name as string,
      description: formState.description,
      template: templateContent || undefined,
      template_type: templateType.value,
      portfolios: formState.portfolios,
    };

    const emailTemplate = await emailTemplateService.create(createDto);

    router.push(`/email/templates-email/${emailTemplate.id}`);
  } catch (error) {
    let errorMessage = "Ha ocurrido un error";

    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();

    message.error(errorMessage);
  }

  loading.value = false;
};

const handleClose = () => emit("update:open", false);
</script>

<style scoped lang="sass">
.container
  padding: 30px 90px
  display: flex
  flex-direction: column
  align-items: center

  .title
    font-size: 24px
    font-weight: 600
    line-height: 32px
    margin-bottom: 12px
    color: $gray-9

  .description
    margin: 0 30px
    color: $gray-8
    margin-bottom: 40px
    text-align: center

  .buttons
    display: grid
    grid-template-columns: 1fr 1fr
    gap: 24px

.info
  width: 100%
  display: flex
  flex-direction: column
  align-items: center
  gap: 8px
  margin-top: 10%

  .info-title
    color: $gray-9
    font-weight: 600
    font-size: 14px

.form
  width: 100%
  min-height: 400px
</style>
